<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Draggable from 'vuedraggable';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import WidgetPreviewHost from '../components/WidgetPreviewHost.vue';
import StepBuilder from './StepBuilder.vue';
import {
  getWidgetLocaleMessages,
  readWidgetLocaleText,
} from 'dashboard/helper/widgetLocale';
import {
  PRE_CHAT_FORM_ELEMENT_TYPES,
  PRE_CHAT_FORM_TYPES,
  clonePreChatStep,
  createPreChatElement,
  createPreChatStep,
  flattenPreChatElements,
  generatePreChatId,
  hasRenderablePreChatSteps,
  normalizePreChatFormType,
} from 'shared/helpers/preChatFormConfig';

// Ticket form has additional text fields
const TICKET_ELEMENT_TYPES = [
  { value: 'information', label: 'Information' },
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'subject', label: 'Subject' },
  { value: 'message', label: 'Message' },
  { value: 'question', label: 'Question' },
  { value: 'choice_list', label: 'Choice list' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'multiple_choice', label: 'Multiple choice list' },
];

// Elements that have choices
const HAS_CHOICES = new Set(['choice_list', 'dropdown', 'multiple_choice']);

// ── Preview page routing ─────────────────────────────────────────────────────
const SCRIPT_OPEN = '\x3cscript\x3e';
const SCRIPT_CLOSE = '\x3c\x2fscript\x3e';

export default {
  name: 'PreChatFormSettings',
  components: { Draggable, NextButton, WidgetPreviewHost, StepBuilder },
  props: {
    inbox: { type: Object, default: () => ({}) },
    mode: { type: String, default: 'prechat' },
  },

  data() {
    return {
      preChatFormTypes: PRE_CHAT_FORM_TYPES,
      preChatFormEnabled: false,
      showAfterCampaign: true,
      preChatFormType: PRE_CHAT_FORM_TYPES.CURRENT,

      // The custom form elements
      elements: [],
      steps: [],
      stepHeaderImageFile: null,
      stepHeaderImageUrl: '',
      stepHeaderTitle: '',
      stepHeaderSubtitle: '',

      // UI state
      showAddMenu: false,
      isSaving: false,

      // Preview
      previewDocHtml: '',
    };
  },

  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    widgetLocaleMessages() {
      return getWidgetLocaleMessages(this.inbox.widget_language);
    },
    isTicketMode() {
      return this.mode === 'ticket';
    },
    activeFormKey() {
      return this.isTicketMode ? 'ticketForm' : 'preChatForm';
    },
    previewRouteName() {
      return this.isTicketMode ? 'ticket-form' : 'prechat-form';
    },
    previewScene() {
      return this.previewRouteName;
    },
    formTitle() {
      return this.isTicketMode ? 'Ticket form' : 'Pre-chat form';
    },
    formDescription() {
      if (this.isTicketMode) {
        return 'Ticket form allows customers to leave details when all agents are offline.';
      }
      return "Pre-chat forms allow you to gather visitor's information before the chat starts.";
    },
    formPreviewNote() {
      return this.isTicketMode
        ? 'Shows the real ticket form inside the widget.'
        : 'Shows the real pre-chat form inside the widget.';
    },

    livePreviewBaseUrl() {
      const s = this.inbox.web_widget_script || '';
      const m = s.match(/var\s+BASE_URL\s*=\s*['"]([^'"]+)['"]/);
      return m?.[1] || window.location.origin;
    },

    livePreviewWebsiteToken() {
      return this.inbox.website_token || '';
    },

    widgetColor() {
      return this.inbox.widget_color || '#0059e1';
    },
    previewDarkMode() {
      const themeConfig = this.inbox.widget_theme_config || {};
      if (this.inbox.widget_theme_preset === 'midnight') {
        return themeConfig.midnightVariant === 'light' ? 'light' : 'dark';
      }
      return themeConfig.darkMode === 'dark' ? 'dark' : 'light';
    },

    // Settings passed to the widget preview iframe
    liveChatwootSettings() {
      const existingTheme = this.inbox.widget_theme_config || {};
      const formConfig = this.buildActiveFormConfig({
        enabled: true,
      });

      return {
        locale: this.inbox.widget_language || 'en',
        position: this.inbox.position || 'right',
        type: 'standard',
        allowThemeOverride: true,
        darkMode: this.previewDarkMode,
        themePreset: this.inbox.widget_theme_preset || 'classic',
        widgetStyle: 'rounded',
        widgetColor: this.widgetColor,
        iconColor: '#ffffff',
        // Inject the custom form into themeConfig so the widget can render it
        themeConfig: {
          ...(existingTheme || {}),
          [this.activeFormKey]: formConfig,
        },
        welcomeTitle: this.inbox.welcome_title || '',
        welcomeDescription: this.inbox.welcome_tagline || '',
        announcementText: this.inbox.announcement_text || '',
        announcementUrl: this.inbox.announcement_url || '',
        announcementBgColor: this.inbox.announcement_bg_color || '#242529',
        announcementTextColor: this.inbox.announcement_text_color || '#ffffff',
        hideMessageBubble: false,
        enableFileUpload: true,
        enableEmojiPicker: true,
        enableEndConversation: true,
        initialRoute: this.previewRouteName,
        previewScene: this.previewScene,
        previewSceneData: {},
      };
    },

    // Add-element menu types (filter out information if already present)
    availableTypes() {
      const hasInfo = this.elements.some(e => e.type === 'information');
      const source = this.isTicketMode
        ? TICKET_ELEMENT_TYPES
        : PRE_CHAT_FORM_ELEMENT_TYPES;
      return source.filter(t => !(hasInfo && t.value === 'information'));
    },
  },

  watch: {
    inbox() {
      this.loadFromInbox();
      this.previewDocHtml = this.buildPreviewDoc();
    },
    elements: {
      deep: true,
      handler() {
        this.pushSettingsToPreview();
      },
    },
    steps: {
      deep: true,
      handler() {
        this.pushSettingsToPreview();
      },
    },
    preChatFormEnabled() {
      this.pushSettingsToPreview();
    },
    preChatFormType() {
      this.pushSettingsToPreview();
    },
    stepHeaderImageUrl() {
      this.pushSettingsToPreview();
    },
    stepHeaderTitle() {
      this.pushSettingsToPreview();
    },
    stepHeaderSubtitle() {
      this.pushSettingsToPreview();
    },
  },

  mounted() {
    this.loadFromInbox();
    this.previewDocHtml = this.buildPreviewDoc();
    document.addEventListener('click', this.onGlobalClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onGlobalClick);
  },

  methods: {
    widgetLocaleText(path, fallback = '') {
      return readWidgetLocaleText(this.widgetLocaleMessages, path, fallback);
    },
    withColon(value) {
      const text = String(value || '')
        .trim()
        .replace(/[:\s]+$/, '');
      return text ? `${text}:` : '';
    },
    placeholderForElementType(type) {
      if (type === 'name') {
        return this.withColon(
          this.widgetLocaleText(
            'PRE_CHAT_FORM.FIELDS.FULL_NAME.LABEL',
            'Full Name'
          )
        );
      }
      if (type === 'email') {
        return this.withColon(
          this.widgetLocaleText(
            'PRE_CHAT_FORM.FIELDS.EMAIL_ADDRESS.LABEL',
            'E-mail'
          )
        );
      }
      if (type === 'phone') {
        return this.withColon(
          this.widgetLocaleText(
            'PRE_CHAT_FORM.FIELDS.PHONE_NUMBER.LABEL',
            'Phone number'
          )
        );
      }
      if (type === 'message') {
        return this.withColon(
          this.widgetLocaleText('PRE_CHAT_FORM.FIELDS.MESSAGE.LABEL', 'Message')
        );
      }
      if (type === 'subject') {
        return 'Subject:';
      }
      if (type === 'question') {
        return this.withColon(
          this.widgetLocaleText(
            'PRE_CHAT_FORM.FIELDS.MESSAGE.LABEL',
            'Question'
          )
        );
      }
      return '';
    },
    cloneElements(elements = []) {
      return (elements || []).map(element => ({
        ...element,
        choices: Array.isArray(element?.choices) ? [...element.choices] : [],
      }));
    },
    buildDefaultStepFormConfig(elements = this.elements) {
      const clonedElements = this.cloneElements(elements);
      const infoElement = clonedElements.find(
        element => element.type === 'information'
      );
      const fieldElements = clonedElements.filter(
        element => element.type !== 'information'
      );
      const stepElements = infoElement
        ? [infoElement, ...fieldElements]
        : fieldElements;

      if (stepElements.length) {
        return [
          createPreChatStep({
            title: 'Tell us more',
            description: '',
            elements: stepElements,
          }),
        ];
      }

      const nameField = createPreChatElement('name');
      nameField.label = 'Name:';
      nameField.required = true;
      const emailField = createPreChatElement('email');
      emailField.label = 'E-mail:';
      emailField.required = true;

      return [
        createPreChatStep({
          title: 'May I have your name?',
          description: '',
          elements: infoElement
            ? [infoElement, nameField, emailField]
            : [nameField, emailField],
        }),
      ];
    },
    buildActiveFormConfig({
      enabled = this.preChatFormEnabled,
      headerImageUrl = this.stepHeaderImageUrl,
    } = {}) {
      if (this.isTicketMode) {
        return {
          enabled,
          elements: this.cloneElements(this.elements),
        };
      }

      return {
        enabled,
        type: this.preChatFormType,
        elements: this.cloneElements(this.elements),
        steps: (this.steps || []).map(clonePreChatStep),
        headerImageUrl: headerImageUrl || '',
        headerTitle: this.stepHeaderTitle || '',
        headerSubtitle: this.stepHeaderSubtitle || '',
      };
    },
    legacyMessageForFormConfig(formConfig) {
      const infoElement = flattenPreChatElements(formConfig).find(
        element => element.type === 'information'
      );
      if (infoElement?.message) {
        return infoElement.message;
      }

      const firstStep = (formConfig.steps || []).find(
        step => Array.isArray(step?.elements) && step.elements.length
      );
      return (
        formConfig.headerSubtitle ||
        firstStep?.description ||
        formConfig.headerTitle ||
        ''
      );
    },
    handleStepHeaderImageSelected({ file, url }) {
      this.stepHeaderImageFile = file;
      this.stepHeaderImageUrl = url || '';
    },
    clearStepHeaderImage() {
      this.stepHeaderImageFile = null;
      this.stepHeaderImageUrl = '';
    },

    // ── Data loading ────────────────────────────────────────────────────────

    loadFromInbox() {
      const cfg = this.inbox.widget_theme_config || {};
      const saved = cfg[this.activeFormKey];
      const normalizedFormType = normalizePreChatFormType(saved?.type);

      this.preChatFormType = normalizedFormType;
      this.stepHeaderImageFile = null;
      this.stepHeaderImageUrl = saved?.headerImageUrl || '';
      this.stepHeaderTitle = saved?.headerTitle || '';
      this.stepHeaderSubtitle = saved?.headerSubtitle || '';

      if (this.isTicketMode) {
        this.preChatFormEnabled = saved?.enabled !== false;
      } else {
        this.preChatFormEnabled = !!this.inbox.pre_chat_form_enabled;
      }
      this.showAfterCampaign = cfg.showPreChatAfterCampaign !== false;

      if (saved && Array.isArray(saved.elements) && saved.elements.length) {
        // Deep-clone to avoid mutating the prop
        this.elements = this.cloneElements(saved.elements).map(e => ({
          ...e,
          collapsed: false,
        }));
      } else if (this.isTicketMode) {
        const ticketInfo =
          "Our agents are not available right now. Please leave a message and we'll get back to you.";
        this.elements = [
          {
            id: generatePreChatId('el'),
            type: 'information',
            message: ticketInfo,
            collapsed: false,
          },
          {
            id: generatePreChatId('el'),
            type: 'name',
            label: 'Your name:',
            required: true,
            collapsed: false,
          },
          {
            id: generatePreChatId('el'),
            type: 'email',
            label: 'E-mail:',
            required: true,
            collapsed: false,
          },
          {
            id: generatePreChatId('el'),
            type: 'subject',
            label: 'Subject:',
            required: true,
            collapsed: false,
          },
          {
            id: generatePreChatId('el'),
            type: 'message',
            label: 'Message:',
            required: true,
            collapsed: false,
          },
        ];
      } else {
        // Default: Information element with the legacy message
        const legacyMsg =
          this.inbox.pre_chat_form_options?.pre_chat_message ||
          "Let's chat! Fill in a few details to get started.";
        this.elements = [
          {
            id: generatePreChatId('el'),
            type: 'information',
            message: legacyMsg,
            collapsed: false,
          },
        ];
      }

      if (!this.isTicketMode) {
        if (hasRenderablePreChatSteps(saved?.steps)) {
          this.steps = saved.steps.map(clonePreChatStep);
        } else {
          this.steps = this.buildDefaultStepFormConfig(this.elements);
        }
      }
    },

    // ── Preview ────────────────────────────────────────────────────────────

    buildPreviewDoc() {
      if (!this.livePreviewWebsiteToken) return '';
      const isMidnight = this.inbox.widget_theme_preset === 'midnight';
      const bg =
        isMidnight && this.inbox.widget_theme_config?.background
          ? this.inbox.widget_theme_config.background
          : isMidnight
            ? '#111111'
            : '#ffffff';
      const settingsJSON = JSON.stringify(this.liveChatwootSettings)
        .split('<')
        .join('\\u003c');
      const baseUrl = JSON.stringify(this.livePreviewBaseUrl);
      const token = JSON.stringify(this.livePreviewWebsiteToken);

      return (
        `<!doctype html><html><head>` +
        `<meta charset="utf-8"/>` +
        `<meta name="viewport" content="width=device-width,initial-scale=1"/>` +
        `<style id="wlab-pcf-style">` +
        `html,body{margin:0;width:100%;height:100%;background:${bg};overflow:hidden;}` +
        `body{position:fixed;inset:0;overflow:hidden;}` +
        `.woot--bubble-holder{visibility:hidden;transition:visibility 0s;}` +
        `</style>` +
        `</head><body>` +
        SCRIPT_OPEN +
        `window.wlabSettings=${settingsJSON};` +
        `window.addEventListener('message',function(e){` +
        `try{var d;if(typeof e.data==='string')d=JSON.parse(e.data);else d=e.data;` +
        `if(d&&d.type==='wlab-settings'){` +
        `var f=document.getElementById('chatwoot_live_chat_widget');if(!f)return;` +
        `f.contentWindow.postMessage('chatwoot-widget:'+JSON.stringify(` +
        `Object.assign({event:'config-set'},d.s,{locale:(d.s&&d.s.locale)||'en',showUnreadMessagesDialog:true,isPreviewUpdate:true})),'*');` +
        `if(d.s.darkMode)f.contentWindow.postMessage('chatwoot-widget:'+` +
        `JSON.stringify({event:'set-color-scheme',darkMode:d.s.darkMode}),'*');` +
        `}}catch(x){}});` +
        `(function(d,t){var B=${baseUrl},T=${token};` +
        `var g=d.createElement(t),s=d.getElementsByTagName(t)[0];` +
        `g.src=B+"/packs/js/sdk.js?v="+Date.now();g.async=true;` +
        `s.parentNode.insertBefore(g,s);` +
        `g.onload=function(){window.wlabSDK.run({websiteToken:T,baseUrl:B});` +
        `setTimeout(function(){if(window.$chatwoot)window.$chatwoot.toggle('open');},800);` +
        `setTimeout(function(){setInterval(function(){` +
        `var wh=document.querySelector('.woot-widget-holder');` +
        `var bh=document.querySelector('.woot--bubble-holder');` +
        `if(!wh||!bh)return;` +
        `bh.style.visibility=wh.classList.contains('woot--hide')?'visible':'hidden';` +
        `},250);},1600);};` +
        `})(document,"script");` +
        SCRIPT_CLOSE +
        `</body></html>`
      );
    },

    pushSettingsToPreview() {
      const iframe = this.$refs.sdkFrame;
      if (!iframe?.contentWindow) return;
      try {
        iframe.contentWindow.postMessage(
          JSON.stringify({
            type: 'wlab-settings',
            s: this.liveChatwootSettings,
          }),
          '*'
        );
      } catch (_) {
        // ignore preview sync failures
      }
    },

    openWebsitePreview() {
      const url = this.inbox.website_url || '';
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    },

    // ── Element management ─────────────────────────────────────────────────

    addElement(type) {
      const base = { id: generatePreChatId('el'), type, collapsed: false };
      if (type === 'information') {
        const infoMessage = this.isTicketMode
          ? "Our agents are not available right now. Please leave a message and we'll get back to you."
          : "Let's chat! Fill in a few details to get started.";
        this.elements.push({ ...base, message: infoMessage });
      } else if (HAS_CHOICES.has(type)) {
        this.elements.push({
          ...base,
          label: 'Question:',
          required: false,
          choices: ['Option 1', 'Option 2'],
        });
      } else {
        const defaults = {
          name: 'Name:',
          email: 'E-mail:',
          phone: 'Phone number:',
          subject: 'Subject:',
          message: 'Message:',
          question: 'Question:',
        };
        this.elements.push({
          ...base,
          label: defaults[type] || '',
          required: false,
        });
      }
      this.showAddMenu = false;
    },
    labelForType(type) {
      const map = {
        information: 'Information',
        name: 'Name',
        email: 'Email',
        phone: 'Phone number',
        subject: 'Subject',
        message: 'Message',
        question: 'Question',
        choice_list: 'Choice list',
        dropdown: 'Dropdown',
        multiple_choice: 'Multiple choice list',
        contact_attribute: 'IC Number',
      };
      return map[type] || type;
    },

    removeElement(id) {
      this.elements = this.elements.filter(e => e.id !== id);
    },

    toggleCollapse(el) {
      el.collapsed = !el.collapsed;
    },

    addChoice(el) {
      el.choices = [...(el.choices || []), ''];
    },

    removeChoice(el, idx) {
      const c = [...(el.choices || [])];
      c.splice(idx, 1);
      el.choices = c;
    },

    // ── UI helpers ─────────────────────────────────────────────────────────

    onGlobalClick(e) {
      if (!this.showAddMenu) return;
      if (!e.target.closest('.pcf-add-wrap')) {
        this.showAddMenu = false;
      }
    },

    cancel() {
      this.loadFromInbox();
    },

    // ── Save ────────────────────────────────────────────────────────────────

    async save() {
      this.isSaving = true;
      try {
        let existingTheme = this.inbox.widget_theme_config || {};
        let headerImageUrl = this.stepHeaderImageUrl;

        if (!this.isTicketMode && this.stepHeaderImageFile && this.inbox?.id) {
          const uploadResponse = await InboxesAPI.uploadPreChatHeaderImage(
            this.inbox.id,
            this.stepHeaderImageFile
          );
          const uploadedTheme = uploadResponse?.data?.widget_theme_config || {};
          const uploadedPreChatForm = uploadedTheme?.preChatForm || {};
          existingTheme = uploadedTheme;
          headerImageUrl = uploadedPreChatForm.headerImageUrl || headerImageUrl;
          this.stepHeaderImageUrl = headerImageUrl;
          this.stepHeaderImageFile = null;
        }

        const activeFormConfig = this.buildActiveFormConfig({
          headerImageUrl,
        });
        const nextTheme = {
          ...existingTheme,
          ...(this.isTicketMode
            ? {}
            : { showPreChatAfterCampaign: this.showAfterCampaign }),
          [this.activeFormKey]: activeFormConfig,
        };

        if (this.isTicketMode) {
          await this.$store.dispatch('inboxes/updateInbox', {
            id: this.inbox.id,
            formData: false,
            channel: {
              widget_theme_config: nextTheme,
            },
          });
          useAlert('Ticket form saved');
        } else {
          // Build backward-compatible pre_chat_form_options
          const flattenedElements = flattenPreChatElements(activeFormConfig);
          const nameEl = flattenedElements.find(e => e.type === 'name');
          const emailEl = flattenedElements.find(e => e.type === 'email');
          const phoneEl = flattenedElements.find(
            e =>
              e.type === 'phone' ||
              e.type === 'phone_number' ||
              e.type === 'phoneNumber'
          );

          const legacyFields = [];
          if (nameEl) {
            legacyFields.push({
              name: 'fullName',
              type: 'text',
              label: nameEl.label || 'Name',
              placeholder: nameEl.label || 'Name',
              enabled: true,
              required: !!nameEl.required,
            });
          }
          if (emailEl) {
            legacyFields.push({
              name: 'emailAddress',
              type: 'email',
              label: emailEl.label || 'E-mail',
              placeholder: emailEl.label || 'E-mail',
              enabled: true,
              required: !!emailEl.required,
            });
          }
          if (phoneEl) {
            legacyFields.push({
              name: 'phoneNumber',
              type: 'text',
              label: phoneEl.label || 'Phone number',
              placeholder: phoneEl.label || 'Phone number',
              enabled: true,
              required: !!phoneEl.required,
            });
          }

          flattenedElements
            .filter(e => e.type === 'contact_attribute' && e.attribute_key)
            .forEach(el => {
              legacyFields.push({
                name: el.attribute_key,
                type: 'text',
                label: el.label || el.attribute_key,
                placeholder: el.label || el.attribute_key,
                enabled: true,
                required: !!el.required,
                field_type: 'contact_attribute',
              });
            });

          await this.$store.dispatch('inboxes/updateInbox', {
            id: this.inbox.id,
            formData: false,
            channel: {
              pre_chat_form_enabled: this.preChatFormEnabled,
              pre_chat_form_options: {
                pre_chat_message:
                  this.legacyMessageForFormConfig(activeFormConfig),
                pre_chat_fields: legacyFields,
              },
              widget_theme_config: nextTheme,
            },
          });
          useAlert('Pre-chat form saved');
        }
      } catch (_) {
        useAlert(
          this.isTicketMode
            ? 'Unable to save ticket form'
            : 'Unable to save pre-chat form'
        );
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <!-- Root — mirrors LanguageSettings two-panel layout exactly -->
  <div class="pcf-page pcf-scroll-layout">
    <div class="pcf-layout">
      <!-- ═══════════════════════════════════════════════
           LEFT PANEL: Settings
           ═══════════════════════════════════════════════ -->
      <div class="pcf-settings-panel">
        <!-- Header bar (56px, white, border-bottom) -->
        <div class="pcf-header-bar">
          <div class="pcf-header-title">{{ formTitle }}</div>
          <div class="pcf-header-actions">
            <!-- Global enable toggle -->
            <button
              type="button"
              class="pcf-toggle-btn"
              @click="preChatFormEnabled = !preChatFormEnabled"
            >
              <span
                class="pcf-switch"
                :class="{ 'pcf-switch--on': preChatFormEnabled }"
              >
                <span class="pcf-switch__track" />
                <span class="pcf-switch__slider" />
              </span>
            </button>
          </div>
        </div>

        <!-- Scrollable content area -->
        <div id="settings-overflow-wrapper" class="pcf-content-scroll">
          <div class="pcf-content-inner">
            <!-- Description -->
            <p class="pcf-desc">
              {{ formDescription }}
              <template v-if="!isTicketMode">
                <a
                  href="https://www.livechat.com/help/chat-surveys/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="pcf-link"
                  >Learn more</a
                >
              </template>
            </p>

            <template v-if="!isTicketMode">
              <div class="pcf-section-label">Form experience</div>
              <div class="pcf-form-type-card">
                <div class="pcf-form-type-toggle">
                  <button
                    type="button"
                    class="pcf-form-type-btn"
                    :class="{
                      'pcf-form-type-btn--active':
                        preChatFormType === preChatFormTypes.CURRENT,
                    }"
                    @click="preChatFormType = preChatFormTypes.CURRENT"
                  >
                    Current form
                  </button>
                  <button
                    type="button"
                    class="pcf-form-type-btn"
                    :class="{
                      'pcf-form-type-btn--active':
                        preChatFormType === preChatFormTypes.STEPS,
                    }"
                    @click="preChatFormType = preChatFormTypes.STEPS"
                  >
                    Step form
                  </button>
                </div>
                <p class="pcf-form-type-hint">
                  Current form keeps the existing single-page experience. Step
                  form lets you split questions into multiple pages with Next /
                  Submit navigation.
                </p>
              </div>
            </template>

            <div class="pcf-section-label">Customize form</div>

            <div class="pcf-cards-area">
              <template
                v-if="
                  isTicketMode || preChatFormType === preChatFormTypes.CURRENT
                "
              >
                <Draggable
                  v-model="elements"
                  item-key="id"
                  handle=".pcf-drag-handle"
                  ghost-class="pcf-ghost"
                  animation="180"
                  tag="div"
                  class="pcf-list"
                >
                  <template #item="{ element: el }">
                    <div class="pcf-card">
                      <div class="pcf-card__head">
                        <button
                          class="pcf-drag-handle"
                          type="button"
                          aria-label="Drag to reorder"
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <circle cx="8" cy="5" r="1" fill="currentColor" />
                            <circle cx="14" cy="5" r="1" fill="currentColor" />
                            <circle cx="8" cy="12" r="1" fill="currentColor" />
                            <circle cx="14" cy="12" r="1" fill="currentColor" />
                            <circle cx="8" cy="19" r="1" fill="currentColor" />
                            <circle cx="14" cy="19" r="1" fill="currentColor" />
                          </svg>
                        </button>

                        <button
                          class="pcf-card__title-btn"
                          type="button"
                          @click="toggleCollapse(el)"
                        >
                          <span
                            class="pcf-chevron"
                            :class="{ 'pcf-chevron--open': !el.collapsed }"
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path d="m6 9 6 6 6-6" />
                            </svg>
                          </span>
                          <span class="pcf-card__type-label">{{
                            labelForType(el.type)
                          }}</span>
                        </button>

                        <div class="pcf-card__actions">
                          <label
                            v-if="el.type !== 'information'"
                            class="pcf-required-check"
                          >
                            <input
                              v-model="el.required"
                              type="checkbox"
                              class="pcf-required-check__input"
                            />
                            <span class="pcf-required-check__box" />
                            <span class="pcf-required-check__label"
                              >Required</span
                            >
                          </label>

                          <button
                            v-if="el.type !== 'contact_attribute'"
                            class="pcf-delete-btn"
                            type="button"
                            aria-label="Delete element"
                            @click="removeElement(el.id)"
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path
                                d="m5 7 1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M4 7h16m-10 4v6m4-6v6M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div v-if="!el.collapsed" class="pcf-card__body">
                        <template v-if="el.type === 'information'">
                          <div class="pcf-field-wrap">
                            <label class="pcf-field-label">Message</label>
                            <textarea
                              v-model="el.message"
                              class="pcf-textarea"
                              rows="3"
                              :placeholder="
                                widgetLocaleText(
                                  'PRE_CHAT_FORM.CAMPAIGN_HEADER',
                                  'Greeting message shown above the form'
                                )
                              "
                            />
                          </div>
                        </template>

                        <template
                          v-else-if="
                            el.type === 'name' ||
                            el.type === 'email' ||
                            el.type === 'phone' ||
                            el.type === 'subject' ||
                            el.type === 'question'
                          "
                        >
                          <div class="pcf-field-wrap">
                            <label class="pcf-field-label">Label</label>
                            <input
                              v-model="el.label"
                              type="text"
                              class="pcf-input"
                              :placeholder="placeholderForElementType(el.type)"
                            />
                          </div>
                        </template>

                        <template v-else-if="el.type === 'contact_attribute'">
                          <div class="pcf-field-wrap">
                            <label class="pcf-field-label">Label</label>
                            <input
                              v-model="el.label"
                              type="text"
                              class="pcf-input"
                              placeholder="IC Number"
                            />
                          </div>
                        </template>

                        <template v-else-if="el.type === 'message'">
                          <div class="pcf-field-wrap">
                            <label class="pcf-field-label">Label</label>
                            <input
                              v-model="el.label"
                              type="text"
                              class="pcf-input"
                              :placeholder="
                                placeholderForElementType('message')
                              "
                            />
                          </div>
                        </template>

                        <template
                          v-else-if="
                            el.type === 'choice_list' ||
                            el.type === 'dropdown' ||
                            el.type === 'multiple_choice'
                          "
                        >
                          <div class="pcf-field-wrap">
                            <label class="pcf-field-label">Label</label>
                            <input
                              v-model="el.label"
                              type="text"
                              class="pcf-input"
                              :placeholder="
                                placeholderForElementType('question')
                              "
                            />
                          </div>
                          <div class="pcf-choices">
                            <div
                              v-for="(choice, ci) in el.choices || []"
                              :key="ci"
                              class="pcf-choice-row"
                            >
                              <div class="pcf-choice-left">
                                <label class="pcf-choice-label-sm"
                                  >Choice #{{ ci + 1 }}</label
                                >
                                <div class="pcf-choice-input-wrap">
                                  <input
                                    v-model="el.choices[ci]"
                                    type="text"
                                    class="pcf-input pcf-input--choice"
                                    :placeholder="`Option ${ci + 1}`"
                                  />
                                </div>
                              </div>
                              <button
                                v-if="(el.choices || []).length > 1"
                                class="pcf-choice-remove"
                                type="button"
                                aria-label="Remove choice"
                                @click="removeChoice(el, ci)"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="2"
                                  stroke-linecap="round"
                                >
                                  <path d="m6 6 12 12M6 18 18 6" />
                                </svg>
                              </button>
                            </div>
                            <button
                              class="pcf-add-answer-btn"
                              type="button"
                              @click="addChoice(el)"
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                              >
                                <path d="M5 12h14m-7-7v14" />
                              </svg>
                              Add answer
                            </button>
                          </div>
                        </template>
                      </div>
                    </div>
                  </template>
                </Draggable>

                <div class="pcf-add-wrap">
                  <button
                    class="pcf-add-element-btn"
                    type="button"
                    @click.stop="showAddMenu = !showAddMenu"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    >
                      <path d="M5 12h14m-7-7v14" />
                    </svg>
                    Add element
                  </button>
                  <transition name="pcf-menu">
                    <ul v-if="showAddMenu" class="pcf-add-menu" @click.stop>
                      <li v-for="t in availableTypes" :key="t.value">
                        <button
                          type="button"
                          class="pcf-add-menu-item"
                          @click="addElement(t.value)"
                        >
                          {{ t.label }}
                        </button>
                      </li>
                    </ul>
                  </transition>
                </div>
              </template>

              <StepBuilder
                v-else
                :steps="steps"
                :header-image-url="stepHeaderImageUrl"
                :header-title="stepHeaderTitle"
                :header-subtitle="stepHeaderSubtitle"
                @update:steps="steps = $event"
                @update:header-title="stepHeaderTitle = $event"
                @update:header-subtitle="stepHeaderSubtitle = $event"
                @imageSelected="handleStepHeaderImageSelected"
                @clearImage="clearStepHeaderImage"
              />

              <div v-if="!isTicketMode" class="pcf-campaign-section">
                <div class="pcf-campaign-row">
                  <h3 class="pcf-campaign-title">
                    Show pre-chat form after campaign
                  </h3>
                  <button
                    type="button"
                    class="pcf-toggle-btn"
                    @click="showAfterCampaign = !showAfterCampaign"
                  >
                    <span
                      class="pcf-switch"
                      :class="{ 'pcf-switch--on': showAfterCampaign }"
                    >
                      <span class="pcf-switch__track" />
                      <span class="pcf-switch__slider" />
                    </span>
                  </button>
                </div>
                <p class="pcf-campaign-desc">
                  You can decide if you want to display pre-chat form after
                  campaign is send to the customer.
                </p>
              </div>
            </div>
          </div>
          <!-- /pcf-content-inner -->
        </div>
        <!-- /pcf-content-scroll -->

        <!-- Sticky save bar -->
        <div class="pcf-save-bar">
          <button class="pcf-cancel-btn" type="button" @click="cancel">
            Cancel
          </button>
          <NextButton
            class="pcf-save-btn"
            type="button"
            label="Save changes"
            :is-loading="isSaving"
            :disabled="isSaving"
            @click="save"
          />
        </div>
      </div>
      <!-- /pcf-settings-panel -->

      <!-- ═══════════════════════════════════════════════
           RIGHT PANEL: Preview
           ═══════════════════════════════════════════════ -->
      <div class="pcf-preview-panel">
        <!-- Preview header bar (gray, 56px) -->
        <div class="pcf-preview-header-bar">
          <div class="pcf-preview-title">Preview</div>
        </div>

        <!-- Preview content area -->
        <div class="pcf-preview-content-area">
          <div class="pcf-preview-scroll-inner">
            <div class="pcf-preview-widget-holder">
              <div class="pcf-preview-widget-stage">
                <div class="pcf-preview-widget-inner">
                  <WidgetPreviewHost
                    :inbox="inbox"
                    :settings="liveChatwootSettings"
                    iframe-class="pcf-preview-iframe"
                    empty-class="pcf-preview-empty"
                  />
                </div>
              </div>
            </div>
            <p class="pcf-preview-test-note">
              <a href="#" @click.prevent="openWebsitePreview">Test it out</a> on
              the chat page
            </p>
          </div>
        </div>
      </div>
      <!-- /pcf-preview-panel -->
    </div>
    <!-- /pcf-layout -->
  </div>
  <!-- /pcf-page -->
</template>

<style scoped>
/* ══════════════════════════════════════════════════════
   Root & layout — mirrors LanguageSettings exactly
   ══════════════════════════════════════════════════════ */
.pcf-page {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #fff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.pcf-page.pcf-scroll-layout {
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.pcf-layout {
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* ══════════════════════════════════════════════════════
   LEFT PANEL
   ══════════════════════════════════════════════════════ */
.pcf-settings-panel {
  flex: 5 1 62.5%;
  min-width: 296px;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background: #fff;
  color: #111827;
  box-sizing: border-box;
}

/* Header bar (56px, matches Language page) */
.pcf-header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 56px;
  background: #fff;
  border-bottom: 1px solid #eeeeef;
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 0 32px 0 21px;
}

.pcf-header-title {
  height: 56px;
  line-height: 56px;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #111827;
  font-weight: 600;
  font-size: 18px;
}

.pcf-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

/* Scrollable content */
.pcf-content-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  overscroll-behavior: contain;
}

.pcf-content-inner {
  padding: 24px 32px 80px;
  box-sizing: border-box;
  max-width: 960px;
}

/* Description */
.pcf-desc {
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 22px;
  color: #6b7280;
}
.pcf-link {
  color: #0059e1;
  text-decoration: none;
}
.pcf-link:hover {
  text-decoration: underline;
}

/* Section label — matches ls-field-label */
.pcf-section-label {
  line-height: 24px;
  color: #111827;
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 15px;
}

.pcf-form-type-card {
  max-width: 705px;
  margin-bottom: 20px;
  padding: 16px;
  border: 1px solid #dde2e6;
  border-radius: 12px;
  background: #fff;
}

.pcf-form-type-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
}

.pcf-form-type-btn {
  min-width: 140px;
  height: 38px;
  padding: 0 16px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;
}

.pcf-form-type-btn--active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.pcf-form-type-hint {
  margin: 12px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: #6b7280;
}

/* Cards area container */
.pcf-cards-area {
  max-width: 705px;
}

/* ── Toggle switch ── */
.pcf-toggle-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}
.pcf-switch {
  display: inline-block;
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: 12px;
  outline: none;
}
.pcf-switch__track {
  position: absolute;
  inset: 0;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.15);
  transition: background 0.25s cubic-bezier(0.33, 0, 0.67, 1);
}
.pcf-switch--on .pcf-switch__track {
  background: #5dd886;
}
.pcf-switch__slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: left 0.25s cubic-bezier(0.33, 0, 0.67, 1);
}
.pcf-switch--on .pcf-switch__slider {
  left: 22px;
}

/* Draggable list */
.pcf-list {
  display: flex;
  flex-direction: column;
}

/* ── Element card ── */
.pcf-card {
  position: relative;
  min-height: 38px;
  margin-bottom: 10px;
  padding: 16px 16px 8px;
  border: 1px solid #dde2e6;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
  user-select: none;
  /* smooth drag animation */
}
.pcf-ghost {
  opacity: 0.4;
  border: 2px dashed #4384f5;
}

/* Card header */
.pcf-card__head {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}

/* Drag handle */
.pcf-drag-handle {
  cursor: grab;
  background: transparent;
  border: none;
  padding: 4px;
  color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.pcf-drag-handle:active {
  cursor: grabbing;
}
.pcf-drag-handle:hover {
  color: rgba(0, 0, 0, 0.7);
}

/* Type label button */
.pcf-card__title-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 4px;
  flex: 1;
  text-align: left;
}
.pcf-chevron {
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.5);
  transition: transform 0.2s ease;
}
.pcf-chevron--open {
  transform: rotate(0deg);
}
.pcf-chevron:not(.pcf-chevron--open) {
  transform: rotate(-90deg);
}
.pcf-card__type-label {
  font-size: 14px;
  font-weight: 700;
  color: #000;
}

/* Actions (required checkbox + delete) */
.pcf-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* Required checkbox */
.pcf-required-check {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #000;
}
.pcf-required-check__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
}
.pcf-required-check__box {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.28);
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition:
    background 0.15s,
    border-color 0.15s;
}
.pcf-required-check__input:checked + .pcf-required-check__box {
  background: #4384f5;
  border-color: #4384f5;
}
.pcf-required-check__input:checked + .pcf-required-check__box::after {
  content: '';
  display: block;
  width: 9px;
  height: 5px;
  border-left: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: rotate(-45deg) translate(0px, -1px);
}
.pcf-required-check__label {
  font-size: 13px;
}

/* Delete button */
.pcf-delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.4);
  transition:
    background 0.15s,
    color 0.15s;
}
.pcf-delete-btn:hover {
  background: rgba(229, 70, 102, 0.1);
  color: #e54666;
}

/* Card body */
.pcf-card__body {
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Field row */
.pcf-field-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.pcf-field-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(230, 70, 0, 0.85); /* LiveChat uses an amber/orange label color */
}

/* Text inputs */
.pcf-input {
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #dde2e6;
  border-radius: 4px;
  background: #fff;
  color: #000;
  font-size: 13px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
}
.pcf-input:focus {
  border-color: #4384f5;
  box-shadow: 0 0 0 3px rgba(67, 132, 245, 0.14);
}

.pcf-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dde2e6;
  border-radius: 4px;
  background: #fff;
  color: #000;
  font-size: 13px;
  outline: none;
  resize: vertical;
  box-sizing: border-box;
  font-family: inherit;
  line-height: 1.45;
  transition: border-color 0.15s;
}
.pcf-textarea:focus {
  border-color: #4384f5;
  box-shadow: 0 0 0 3px rgba(67, 132, 245, 0.14);
}

/* Choices area */
.pcf-choices {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 16px;
  border-left: 2px solid #dde2e6;
}
.pcf-choice-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.pcf-choice-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}
.pcf-choice-label-sm {
  font-size: 11px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.5);
}
.pcf-input--choice {
  flex: 1;
}

.pcf-choice-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 38px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.4);
  transition: color 0.15s;
  flex-shrink: 0;
}
.pcf-choice-remove:hover {
  color: #e54666;
}

.pcf-add-answer-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #dde2e6;
  border-radius: 4px;
  background: #fff;
  color: #000;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  align-self: flex-start;
  transition: background 0.15s;
}
.pcf-add-answer-btn:hover {
  background: rgba(0, 0, 0, 0.04);
}

/* ── Add element ── */
.pcf-add-wrap {
  position: relative;
  margin-top: 4px;
}
.pcf-add-element-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 16px;
  border: 1px solid #dde2e6;
  border-radius: 4px;
  background: #fff;
  color: #000;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}
.pcf-add-element-btn:hover {
  background: rgba(0, 0, 0, 0.04);
}

.pcf-add-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 1000;
  min-width: 180px;
  background: #fff;
  border: 1px solid #dde2e6;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 4px 0;
  list-style: none;
  margin: 0;
}
.pcf-add-menu-item {
  display: block;
  width: 100%;
  padding: 9px 14px;
  background: transparent;
  border: none;
  text-align: left;
  font-size: 13px;
  color: #000;
  cursor: pointer;
  transition: background 0.1s;
}
.pcf-add-menu-item:hover {
  background: rgba(67, 132, 245, 0.07);
}

/* Menu animation */
.pcf-menu-enter-active,
.pcf-menu-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}
.pcf-menu-enter-from,
.pcf-menu-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* ── Campaign section — lives inside the scroll area, scrolls naturally ── */
.pcf-campaign-section {
  /* No position stickiness — scrolls with content */
  margin-top: 24px;
  padding: 20px 0 0;
  border-top: 1px solid #dde2e6;
  /* Extra bottom padding so the footer doesn't cover it */
  padding-bottom: 16px;
}
.pcf-campaign-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.pcf-campaign-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #000;
}
.pcf-campaign-desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.6);
}

/* ══════════════════════════════════════════════════════
   Save bar (sticky bottom) — mirrors Language page
   ══════════════════════════════════════════════════════ */
.pcf-save-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 32px;
  border-top: 1px solid #eeeeef;
  background: #fff;
  flex-shrink: 0;
}

.pcf-cancel-btn {
  height: 36px;
  padding: 0 16px;
  border: 1px solid #bcc6d0;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s;
}
.pcf-cancel-btn:hover {
  background: #f5f5f5;
}

.pcf-save-bar :deep(.pcf-save-btn) {
  height: 36px !important;
  padding: 0 16px !important;
  border-radius: 8px !important;
  background: #0059e1 !important;
  border: 1px solid #0059e1 !important;
  color: #fff !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  box-shadow: none !important;
}

.pcf-save-bar :deep(.pcf-save-btn:hover) {
  background: #0047c2 !important;
  border-color: #0047c2 !important;
}

/* ══════════════════════════════════════════════════════
   RIGHT PANEL (Preview) — mirrors LanguageSettings exactly
   ══════════════════════════════════════════════════════ */
.pcf-preview-panel {
  box-sizing: border-box;
  height: 100%;
  border-left: 1px solid #eeeeef;
  min-width: 0;
  flex: 3 1 37.5%;
  z-index: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background: #f5f5f5;
}

.pcf-preview-header-bar {
  display: flex;
  width: 100%;
  height: 56px;
  align-items: center;
  background: #f5f5f5;
  border-bottom: 1px solid #eeeeef;
  flex-shrink: 0;
}

.pcf-preview-title {
  height: 56px;
  line-height: 56px;
  letter-spacing: 0.3px;
  color: #111827;
  font-weight: 600;
  font-size: 18px;
  margin-left: 20px;
}

.pcf-preview-content-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  background: #f5f5f5;
}

.pcf-preview-scroll-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

/* Widget holder — matches LiveChat .css-ht54w1 */
.pcf-preview-widget-holder {
  height: 620px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  width: 360px;
  overflow: hidden;
  padding: 36px;
  margin: auto;
}

.pcf-preview-widget-stage {
  flex: 1 1 100%;
  width: 100%;
  height: 100%;
  position: relative;
  display: block;
  background: transparent;
  max-width: 320px;
}

.pcf-preview-widget-inner {
  position: relative;
  flex: 1 1 0%;
  height: 100%;
}

/* iframe scaled 0.8 — matches Language page */
.pcf-preview-iframe {
  width: 360px;
  height: 682px;
  border: none;
  display: block;
  border-radius: 16px;
  transform: scale(0.8);
  transform-origin: 0 0;
  box-shadow:
    rgba(0, 0, 0, 0.05) 0px 0.48px 2.41px -0.38px,
    rgba(0, 0, 0, 0.17) 0px 4px 20px -0.75px;
}

.pcf-preview-empty {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
  text-align: center;
}

.pcf-preview-test-note {
  font-size: 13px;
  color: #6b7280;
  text-align: center;
  margin: 8px 0 16px;
}

.pcf-preview-test-note a {
  color: #0059e1;
  text-decoration: none;
}

.pcf-preview-test-note a:hover {
  text-decoration: underline;
}

/* Force split-panel independent scrolling, matching customization pages */
.pcf-page.pcf-scroll-layout > .pcf-layout {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}

.pcf-page.pcf-scroll-layout > .pcf-layout > .pcf-settings-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  flex: 5 1 62.5%;
}

.pcf-page.pcf-scroll-layout
  > .pcf-layout
  > .pcf-settings-panel
  > .pcf-header-bar {
  flex-shrink: 0;
  height: 56px;
  min-height: 56px;
}

.pcf-page.pcf-scroll-layout
  > .pcf-layout
  > .pcf-settings-panel
  > .pcf-save-bar {
  flex-shrink: 0;
}

.pcf-page.pcf-scroll-layout
  > .pcf-layout
  > .pcf-settings-panel
  > .pcf-content-scroll {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
}

.pcf-page.pcf-scroll-layout > .pcf-layout > .pcf-preview-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  flex: 3 1 37.5%;
}

.pcf-page.pcf-scroll-layout
  > .pcf-layout
  > .pcf-preview-panel
  > .pcf-preview-header-bar {
  flex-shrink: 0;
  height: 56px;
  min-height: 56px;
}

.pcf-page.pcf-scroll-layout
  > .pcf-layout
  > .pcf-preview-panel
  > .pcf-preview-content-area {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
}
</style>
